import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import BalanceOverview from "@/components/BalanceOverview";
import CashFlowSection from "@/components/CashFlowSection";
import SubscriptionDetector from "@/components/SubscriptionDetector";
import SpendAlerts from "@/components/SpendAlerts";
import TransactionsLedger from "@/components/TransactionsLedger";
import SpendingInsights from "@/components/SpendingInsights";
import SavingsNudges from "@/components/SavingsNudges";
import BetterDeals from "@/components/BetterDeals";
import TrueLayerConnectModal from "@/components/TrueLayerConnectModal";
import BudgetSettings from "@/pages/BudgetSettings";
import StudentHub from "@/pages/StudentHub";
import ProHub from "@/pages/ProHub";
import Billing from "@/pages/Billing";
import DealsAdmin from "@/pages/DealsAdmin";
import { useCurrency } from "@/context/CurrencyContext";
import ProfileTypeModal from "@/components/ProfileTypeModal";
import WelcomeTour from "@/components/WelcomeTour";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Landmark, ShieldCheck, Loader2 } from "lucide-react";

export default function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [subs, setSubs] = useState(null);
  const [insights, setInsights] = useState(null);
  const [nudges, setNudges] = useState(null);
  const [alertsData, setAlertsData] = useState({ alerts: [], unread: 0 });
  const [thresholdsData, setThresholdsData] = useState({ thresholds: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [paySession] = useState(() => (window.location.pathname === "/payment/success" ? new URLSearchParams(window.location.search).get("session_id") : null));
  const [view, setView] = useState(() => (window.location.pathname.startsWith("/payment/") ? "billing" : "dashboard"));
  const [profileOpen, setProfileOpen] = useState(false);
  const { user } = useAuth();
  const { code } = useCurrency();
  const isStudent = user?.profile_type === "student";
  const isPro = user?.profile_type === "professional";
  const needsProfile = !user?.profile_type;
  const [tourReplay, setTourReplay] = useState(false);
  const tourOpen = !needsProfile && !profileOpen && (tourReplay || !user?.tour_completed);

  useEffect(() => {
    if ((view === "student" && !isStudent) || (view === "pro" && !isPro)) setView("dashboard");
  }, [view, isStudent, isPro]);

  const loadAll = useCallback(async () => {
    try {
      const [d, s, a, t] = await Promise.all([
        api.get("/dashboard"),
        api.get("/subscriptions"),
        api.get("/spend-alerts"),
        api.get("/spend-alerts/thresholds"),
      ]);
      setDashboard(d.data);
      setSubs(s.data);
      setAlertsData(a.data);
      setThresholdsData(t.data);
      api.get("/insights").then((r) => setInsights(r.data)).catch(() => {});
      api.get("/savings-nudges").then((r) => setNudges(r.data)).catch(() => {});
    } catch {
      // token interceptor handles auth
    } finally {
      setLoading(false);
    }
  }, []);

  const reload = useCallback(() => {
    loadAll();
    setReloadKey((k) => k + 1);
  }, [loadAll]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (window.location.pathname === "/payment/cancel") toast.info("Checkout cancelled. You haven't been charged.");
    if (window.location.pathname.startsWith("/payment/") && !paySession) window.history.replaceState({}, "", "/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected")) {
      toast.success("Bank connected via TrueLayer");
      reload();
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("bank_error")) {
      toast.error("Bank connection didn't complete. Please try again or use the demo accounts.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToAlerts = () => document.getElementById("spend-alerts-section")?.scrollIntoView({ behavior: "smooth", block: "center" });

  const hasAccounts = (dashboard?.accounts?.length || 0) > 0;

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <div className="mm-glow" />
      <Navbar
        accountCount={dashboard?.account_count || 0}
        unreadAlerts={alertsData.unread}
        onConnect={() => setConnectOpen(true)}
        onOpenAlerts={scrollToAlerts}
        onOpenBudget={() => { setView("budget"); window.scrollTo({ top: 0 }); }}
        onOpenStudent={() => { setView("student"); window.scrollTo({ top: 0 }); }}
        onOpenPro={() => { setView("pro"); window.scrollTo({ top: 0 }); }}
        onOpenBilling={() => { setView("billing"); window.scrollTo({ top: 0 }); }}
        onOpenAdmin={() => { setView("admin"); window.scrollTo({ top: 0 }); }}
        onOpenProfileType={() => setProfileOpen(true)}
        onReplayTour={() => setTourReplay(true)}
        view={view}
      />

      <main key={code} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative z-10">
        {view === "admin" && user?.is_admin ? (
          <DealsAdmin onBack={() => setView("dashboard")} />
        ) : view === "billing" ? (
          <Billing onBack={() => setView("dashboard")} sessionId={paySession} onSessionHandled={() => window.history.replaceState({}, "", "/")} />
        ) : view === "budget" ? (
          <BudgetSettings onBack={() => setView("dashboard")} onChanged={reload} />
        ) : view === "student" && isStudent ? (
          <StudentHub onBack={() => setView("dashboard")} />
        ) : view === "pro" && isPro ? (
          <ProHub onBack={() => setView("dashboard")} />
        ) : loading ? (
          <div className="h-[60vh] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#00C896]" />
          </div>
        ) : !hasAccounts ? (
          <div className="mm-glow-card p-10 sm:p-16 text-center max-w-2xl mx-auto mt-8 mm-fade-up" data-testid="empty-state">
            <span className="mx-auto h-16 w-16 rounded-2xl bg-[#00C896]/15 flex items-center justify-center text-[#00C896]">
              <Landmark className="h-8 w-8" />
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-6">Connect your first UK bank</h2>
            <p className="text-slate-400 mt-3 max-w-md mx-auto">
              Link an account through TrueLayer Open Banking to unlock your balances, cash flow, subscription radar and spend alerts.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-[#4DE3BF]">
              <ShieldCheck className="h-4 w-4" /> Read-only · FCA regulated · Bank-grade encryption
            </div>
            <Button
              data-testid="empty-connect-button"
              onClick={() => setConnectOpen(true)}
              className="mt-6 h-11 px-6 rounded-xl bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold"
            >
              Connect a bank
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4 sm:gap-5 mm-fade-up">
            <div className="col-span-12 lg:col-span-8">
              <BalanceOverview data={dashboard} onReload={reload} />
            </div>
            <div className="col-span-12 lg:col-span-4">
              <CashFlowSection data={dashboard} />
            </div>

            <div className="col-span-12">
              <SavingsNudges data={nudges} />
            </div>

            <div className="col-span-12">
              <BetterDeals reloadKey={reloadKey} />
            </div>

            <div className="col-span-12">
              <SpendingInsights data={insights} />
            </div>

            <div className="col-span-12 lg:col-span-6">
              <SubscriptionDetector data={subs} onReload={reload} />
            </div>
            <div className="col-span-12 lg:col-span-6">
              <SpendAlerts
                thresholds={thresholdsData.thresholds}
                categories={thresholdsData.categories}
                budgetCategories={thresholdsData.budget_categories}
                alerts={alertsData.alerts}
                unread={alertsData.unread}
                onReload={reload}
                onManage={() => { setView("budget"); window.scrollTo({ top: 0 }); }}
              />
            </div>

            <div className="col-span-12">
              <TransactionsLedger
                accounts={dashboard?.accounts || []}
                categories={thresholdsData.categories}
                reloadKey={reloadKey}
                onReload={reload}
              />
            </div>
          </div>
        )}

        <footer className="mt-10 py-6 text-center text-xs text-slate-600 border-t border-white/6">
          Finlo · a product by Ventiqa Solutions Limited · {code === "GBP" || view === "billing" ? "All amounts in £ GBP" : `Amounts shown in ${code}, converted from £ GBP at the daily exchange rate`}
        </footer>
      </main>

      <ProfileTypeModal open={needsProfile || profileOpen} onOpenChange={setProfileOpen} required={needsProfile} />
      <WelcomeTour
        open={tourOpen}
        onClose={() => setTourReplay(false)}
        actions={{
          connect: () => setConnectOpen(true),
          budget: () => { setView("budget"); window.scrollTo({ top: 0 }); },
          student: () => { setView("student"); window.scrollTo({ top: 0 }); },
          pro: () => { setView("pro"); window.scrollTo({ top: 0 }); },
        }}
      />
      <TrueLayerConnectModal open={connectOpen} onOpenChange={setConnectOpen} onConnected={reload} />
    </div>
  );
}
